import React from 'react';
import { useTranslation } from 'react-i18next';
import { X, Check, Palette } from 'lucide-react';
import { GROUP_BACKGROUND_THEMES, groupBackgroundStyle } from '../../../utils/groupBackgroundThemes';

// Lightweight preset-swatch picker for a group's page background theme --
// deliberately simpler than AvatarSelectorModal.jsx (no per-user upload,
// no image-loading/fallback state -- just picking one of
// GROUP_BACKGROUND_THEMES). Split into two labeled sections since the set
// now mixes flat gradients and illustrated banner images (2026-07-19).
const ThemeSection = ({ title, themes, currentTheme, saving, onSelect }) => {
  const { t } = useTranslation();
  return (
  <div>
    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400 mb-2">{title}</h3>
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {themes.map((theme) => {
        const isSelected = currentTheme === theme.id;
        return (
          <button
            key={theme.id}
            onClick={() => !saving && onSelect(theme.id)}
            disabled={saving}
            className={`relative h-20 rounded-2xl border-2 transition-all overflow-hidden disabled:opacity-60 disabled:cursor-not-allowed ${
              isSelected ? 'border-white shadow-lg scale-[0.98]' : 'border-transparent hover:scale-[1.02]'
            }`}
            style={groupBackgroundStyle(theme.id)}
            aria-label={t('groups.themePicker.selectThemeAria', { themeName: theme.name })}
            aria-pressed={isSelected}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
            <span className="absolute bottom-1.5 left-2 text-xs font-semibold text-white drop-shadow">
              {theme.name}
            </span>
            {isSelected && (
              <div className="absolute top-2 right-2 bg-black/40 backdrop-blur-sm rounded-full p-1">
                <Check className="w-3.5 h-3.5 text-white" />
              </div>
            )}
          </button>
        );
      })}
    </div>
  </div>
  );
};

const GroupBackgroundThemePicker = ({ isOpen, onClose, onSelect, currentTheme, saving = false }) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  const allThemes = Object.values(GROUP_BACKGROUND_THEMES);
  const gradientThemes = allThemes.filter((t) => t.type === 'gradient');
  const imageThemes = allThemes.filter((t) => t.type === 'image');

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 rounded-3xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden border border-gray-200 dark:border-gray-700/50 flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700/50 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-violet-600/20 rounded-xl border border-violet-500/20">
              <Palette className="w-5 h-5 text-violet-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">{t('groups.themePicker.title')}</h2>
              <p className="text-xs text-gray-600 dark:text-gray-400">{t('groups.themePicker.visibleToEveryone')}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-xl transition-colors" aria-label={t('common.close')}>
            <X className="w-5 h-5 text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-white" />
          </button>
        </div>

        <div className="p-5 space-y-5 overflow-y-auto">
          <ThemeSection title={t('groups.themePicker.illustratedBanners')} themes={imageThemes} currentTheme={currentTheme} saving={saving} onSelect={onSelect} />
          <ThemeSection title={t('groups.themePicker.gradients')} themes={gradientThemes} currentTheme={currentTheme} saving={saving} onSelect={onSelect} />
        </div>
      </div>
    </div>
  );
};

export default GroupBackgroundThemePicker;
